import React, { useState, useEffect, useMemo, useRef } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import { Container, Card, Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import { collection, collectionGroup, query, where, getFirestore, orderBy } from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import firebaseApp from "../../firebase/credenciales";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaSearch, FaMapMarkedAlt } from "react-icons/fa";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const db = getFirestore(firebaseApp);
const GPS_SAMPLE_SECONDS = 30;
const SPLIT_GAP_SECONDS = 15 * 60;
const MERGE_WINDOW_MINUTES = 90;
const TRAMO_ANCHOR_POINT = {
  lat: -13.731399859912788,
  lng: -76.21666510003105,
};
const ANCHOR_MAX_DISTANCE_METERS = 1200;

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const redIcon = L.divIcon({
  className: "custom-red-marker",
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#e53935;border:2px solid #111827;"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const startIcon = L.divIcon({
  className: "custom-start-marker",
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#10b981;border:2px solid #ffffff;"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const endIcon = L.divIcon({
  className: "custom-end-marker",
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#111827;border:2px solid #ffffff;"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const empresaIcon = L.divIcon({
  className: "custom-empresa-marker",
  html: '<div style="padding:2px 6px;background:#0f172a;color:#fff;border-radius:10px;font-size:10px;font-weight:700;">EMPRESA</div>',
  iconSize: [62, 20],
  iconAnchor: [31, 10],
});

L.Marker.prototype.options.icon = DefaultIcon;

function ChangeView({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds, map]);
  return null;
}

const isDropoffPoint = (point) => {
  const candidates = [
    point?.tipo,
    point?.metadata?.tipo,
    point?.metadata?.eventoTipo,
    point?.metadata?.movimiento,
    point?.metadata?.accion,
    point?.eventoTipo,
    point?.movimiento,
    point?.accion,
    point?.tipoRegistro,
    point?.metadata?.tipoRegistro,
    point?.metadata?.descripcion,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  if (point?.metadata?.esBajada === true || point?.esBajada === true) return true;

  return candidates.some(
    (value) =>
      value.includes("salida") ||
      value.includes("bajada") ||
      value.includes("dropoff") ||
      value.includes("descenso") ||
      value.includes("bajo")
  );
};

const isBoardingPoint = (point) => {
  const candidates = [
    point?.tipo,
    point?.metadata?.tipo,
    point?.metadata?.eventoTipo,
    point?.metadata?.movimiento,
    point?.metadata?.accion,
    point?.eventoTipo,
    point?.movimiento,
    point?.accion,
    point?.tipoRegistro,
    point?.metadata?.tipoRegistro,
    point?.metadata?.descripcion,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  if (point?.metadata?.esSubida === true || point?.esSubida === true) return true;

  return candidates.some(
    (value) =>
      value.includes("ingreso") ||
      value.includes("subida") ||
      value.includes("embarque") ||
      value.includes("abordo") ||
      value.includes("abordaje")
  );
};

const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const ReporteTrazabilidad = ({ usuario }) => {
  const [buses, setBuses] = useState([]);
  const [selectedBus, setSelectedBus] = useState("");
  const [fechaDia, setFechaDia] = useState(new Date().toISOString().slice(0, 10));
  const [coordenadas, setCoordenadas] = useState([]);
  const [coordenadasBaseDia, setCoordenadasBaseDia] = useState([]);
  const [segmentosDiaRaw, setSegmentosDiaRaw] = useState([]);
  const [segmentosMapa, setSegmentosMapa] = useState([]);
  const [dropoffCoords, setDropoffCoords] = useState([]);
  const [tramosDia, setTramosDia] = useState([]);
  const [selectedTramo, setSelectedTramo] = useState("todos");
  const [distanciasAnchor, setDistanciasAnchor] = useState({ inicio: null, fin: null });
  const [metricasTramo, setMetricasTramo] = useState({
    kmRecorridos: 0,
    distanciaMaxEmpresaM: 0,
    estado: "Sin datos",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const rucEmpresa = usuario?.rucEmpresa || usuario?.ruc;

  useEffect(() => {
    if (rucEmpresa) cargarBuses();
  }, [rucEmpresa]);

  const cargarBuses = async () => {
    try {
      establecerOrigenConsumo("asistencia:trazabilidad");
      const busesRef = collection(db, "buses");
      const q = query(busesRef, where("rucEmpresa", "==", String(rucEmpresa)));
      const snapshot = await getDocs(q);
      const busesList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBuses(busesList);
    } catch (err) {
      console.error("Error cargando buses:", err);
      setError("Error al cargar la lista de buses.");
    }
  };

  const formatDateLabel = (timestamp) =>
    new Date(timestamp).toLocaleDateString("es-PE", { year: "numeric", month: "2-digit", day: "2-digit" });
  const formatTimeLabel = (timestamp) =>
    new Date(timestamp).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const formatDurationMinutes = (startTs, endTs) => {
    const minutes = Math.max(0, Math.round((endTs - startTs) / 60000));
    return `${minutes} min`;
  };

  const splitByTimeAndDistance = (
    coords,
    gapMinutes = SPLIT_GAP_SECONDS / 60
  ) => {
    if (!coords || coords.length === 0) return [];
    const segments = [];
    let current = [coords[0]];

    for (let i = 1; i < coords.length; i++) {
      const diffMinutes = (coords[i].normalizedTime - coords[i - 1].normalizedTime) / (1000 * 60);
      if (diffMinutes > gapMinutes) {
        segments.push(current);
        current = [];
      }
      current.push(coords[i]);
    }
    segments.push(current);
    return segments;
  };

  const parseFechaHoraToMs = (value) => {
    if (!value) return null;
    // Formato esperado: "YYYY-MM-DD HH:mm:ss"
    const normalized = String(value).trim().replace(" ", "T");
    const date = new Date(normalized);
    const ms = date.getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const mergeSegmentsByWindow = (segments, windowMinutes = MERGE_WINDOW_MINUTES) => {
    if (!segments || segments.length <= 1) return segments || [];

    const merged = [];
    let current = [...segments[0]];

    for (let i = 1; i < segments.length; i++) {
      const next = segments[i];
      const currentStart = current[0]?.normalizedTime || 0;
      const nextEnd = next[next.length - 1]?.normalizedTime || 0;
      const combinedMinutes = (nextEnd - currentStart) / (1000 * 60);

      if (combinedMinutes <= windowMinutes) {
        current = [...current, ...next];
      } else {
        merged.push(current);
        current = [...next];
      }
    }

    merged.push(current);
    return merged;
  };

  const orientarSegmentoDesdeAnchor = (segmento) => {
    if (!segmento || segmento.length < 2) return segmento || [];
    const primero = segmento[0];
    const ultimo = segmento[segmento.length - 1];
    const distInicio = haversineMeters(
      primero.latitud,
      primero.longitud,
      TRAMO_ANCHOR_POINT.lat,
      TRAMO_ANCHOR_POINT.lng
    );
    const distFin = haversineMeters(
      ultimo.latitud,
      ultimo.longitud,
      TRAMO_ANCHOR_POINT.lat,
      TRAMO_ANCHOR_POINT.lng
    );
    // Si el final está más cerca del anchor, invertimos para trazar desde ahí
    return distFin < distInicio ? [...segmento].reverse() : segmento;
  };

  const orientarRutaCompletaDesdeAnchor = (coords = []) => {
    if (!coords || coords.length < 2) return coords || [];
    const inicio = coords[0];
    const fin = coords[coords.length - 1];
    const distInicio = haversineMeters(
      inicio.latitud,
      inicio.longitud,
      TRAMO_ANCHOR_POINT.lat,
      TRAMO_ANCHOR_POINT.lng
    );
    const distFin = haversineMeters(
      fin.latitud,
      fin.longitud,
      TRAMO_ANCHOR_POINT.lat,
      TRAMO_ANCHOR_POINT.lng
    );
    return distFin < distInicio ? [...coords].reverse() : coords;
  };

  const recortarRutaParaAnclarExtremo = (coords = []) => {
    if (!coords || coords.length < 3) return coords || [];

    const distInicio = haversineMeters(coords[0].latitud, coords[0].longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng);
    const distFin = haversineMeters(
      coords[coords.length - 1].latitud,
      coords[coords.length - 1].longitud,
      TRAMO_ANCHOR_POINT.lat,
      TRAMO_ANCHOR_POINT.lng
    );
    if (distInicio <= ANCHOR_MAX_DISTANCE_METERS || distFin <= ANCHOR_MAX_DISTANCE_METERS) {
      return coords;
    }

    let nearestIdx = 0;
    let nearestDist = Number.POSITIVE_INFINITY;
    coords.forEach((p, idx) => {
      const d = haversineMeters(p.latitud, p.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng);
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = idx;
      }
    });

    // Si incluso el punto más cercano está demasiado lejos, no recortar para no falsear ruta.
    if (nearestDist > ANCHOR_MAX_DISTANCE_METERS * 2) {
      return coords;
    }

    const tramoA = coords.slice(0, nearestIdx + 1);
    const tramoB = coords.slice(nearestIdx);
    const durA = (tramoA[tramoA.length - 1].normalizedTime - tramoA[0].normalizedTime) || 0;
    const durB = (tramoB[tramoB.length - 1].normalizedTime - tramoB[0].normalizedTime) || 0;

    // Conservamos el subtramo más largo y luego lo orientamos para que inicie/cierre en empresa.
    return durB >= durA ? tramoB : tramoA;
  };

  const classifyTramoByAnchor = (segmento) => {
    if (!segmento || segmento.length < 2) return "Tramo";
    const start = segmento[0];
    const end = segmento[segmento.length - 1];
    const startDist = haversineMeters(start.latitud, start.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng);
    const endDist = haversineMeters(end.latitud, end.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng);
    if (endDist < startDist) return "Ingreso";
    if (startDist < endDist) return "Salida";
    return "Tramo";
  };

  const samplePointsForRouting = (points, maxPoints = 40) => {
    if (!points || points.length <= maxPoints) return points || [];
    const sampled = [];
    for (let i = 0; i < maxPoints; i++) {
      const index = Math.round((i * (points.length - 1)) / (maxPoints - 1));
      sampled.push(points[index]);
    }
    return sampled;
  };

  const mapGeometryToTimedCoords = (geometry, segment, segmentIndex) => {
    const startTime = segment[0].normalizedTime;
    const endTime = segment[segment.length - 1].normalizedTime;
    const totalSteps = Math.max(geometry.length - 1, 1);

    return geometry.map((coord, idx) => {
      const ratio = idx / totalSteps;
      return {
        id: `snap_${segmentIndex}_${idx}`,
        latitud: coord[1],
        longitud: coord[0],
        normalizedTime: startTime + (endTime - startTime) * ratio,
        placa: segment[0].placa,
        busId: segment[0].busId || segment[0].metadata?.busId,
      };
    });
  };

  const snapSegmentToRoad = async (segment, segmentIndex) => {
    if (!segment || segment.length < 2) return segment || [];

    const routingPoints = samplePointsForRouting(segment, 40);
    const waypoints = routingPoints.map((p) => `${p.longitud},${p.latitud}`);

    const matchProviders = [
      "https://router.project-osrm.org/match/v1/driving",
      "https://routing.openstreetmap.de/routed-car/match/v1/driving",
    ];
    const routeProviders = [
      "https://router.project-osrm.org/route/v1/driving",
      "https://routing.openstreetmap.de/routed-car/route/v1/driving",
    ];

    for (const provider of matchProviders) {
      try {
        const url = `${provider}/${waypoints.join(";")}?overview=full&geometries=geojson&tidy=true&gaps=split&annotations=false`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data.code === "Ok" && Array.isArray(data.matchings) && data.matchings.length > 0) {
          const geometry = data.matchings[0]?.geometry?.coordinates || [];
          if (geometry.length > 1) return mapGeometryToTimedCoords(geometry, segment, segmentIndex);
        }
      } catch (e) {
        console.warn(`No se pudo map-matching en segmento ${segmentIndex} (${provider}):`, e);
      }
    }

    for (const provider of routeProviders) {
      try {
        const url = `${provider}/${waypoints.join(";")}?overview=full&geometries=geojson&alternatives=false&steps=false&continue_straight=true`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data.code === "Ok" && data.routes && data.routes.length > 0) {
          const geometry = data.routes[0].geometry.coordinates;
          if (geometry && geometry.length > 1) return mapGeometryToTimedCoords(geometry, segment, segmentIndex);
        }
      } catch (e) {
        console.warn(`No se pudo snap por route en segmento ${segmentIndex} (${provider}):`, e);
      }
    }

    return segment;
  };

  const snapCoordsToRoad = async (coords) => {
    const segments = mergeSegmentsByWindow(
      splitByTimeAndDistance(coords, SPLIT_GAP_SECONDS / 60),
      MERGE_WINDOW_MINUTES
    );
    const snapped = await Promise.all(segments.map((s, i) => snapSegmentToRoad(s, i)));
    return snapped.flat();
  };

  const aplicarTramoEnMapa = async (coordsDia, tramoId, segmentosSource = null) => {
    const segmentados = Array.isArray(segmentosSource) && segmentosSource.length > 0
      ? segmentosSource
      : mergeSegmentsByWindow(
          splitByTimeAndDistance(coordsDia, SPLIT_GAP_SECONDS / 60),
          MERGE_WINDOW_MINUTES
        );
    const segmentosSeleccionados =
      tramoId === "todos"
        ? segmentados
        : [segmentados[Number(tramoId)] || []].filter((s) => s.length > 0);
    const tramoFiltrado = segmentosSeleccionados.flat();

    const dropoffs = tramoFiltrado.filter((c) => isDropoffPoint(c));

    const DISTANCIA_MINIMA_METROS = 5;
    const segmentosFiltrados = segmentosSeleccionados
      .map((segmento) => {
        if (!segmento || segmento.length === 0) return [];
        const cleaned = [segmento[0]];
        let lastSaved = segmento[0];
        for (let i = 1; i < segmento.length; i++) {
          const current = segmento[i];
          const prev = lastSaved;
          const dist = haversineMeters(current.latitud, current.longitud, prev.latitud, prev.longitud);
          const timeDiffMinutes = (current.normalizedTime - prev.normalizedTime) / (1000 * 60);
          if (dist >= DISTANCIA_MINIMA_METROS || timeDiffMinutes > 5) {
            cleaned.push(current);
            lastSaved = current;
          }
        }
        return orientarSegmentoDesdeAnchor(cleaned);
      })
      .filter((s) => s.length > 0);

    const segmentosSnapped = await Promise.all(
      segmentosFiltrados.map((segmento, idx) =>
        segmento.length > 1 ? snapSegmentToRoad(segmento, idx) : Promise.resolve(segmento)
      )
    );
    let coordsForMap = segmentosSnapped.flat();
    coordsForMap = orientarRutaCompletaDesdeAnchor(coordsForMap);
    coordsForMap = recortarRutaParaAnclarExtremo(coordsForMap);
    coordsForMap = orientarRutaCompletaDesdeAnchor(coordsForMap);

    const startTs = coordsForMap[0]?.normalizedTime || 0;
    const endTs = coordsForMap[coordsForMap.length - 1]?.normalizedTime || 0;
    const maxDistanceToRouteMeters = 300;
    const dropoffsFiltered = dropoffs.filter((d) => {
      const inTimeWindow = d.normalizedTime >= startTs && d.normalizedTime <= endTs;
      if (!inTimeWindow) return false;
      const minDist = coordsForMap.reduce((acc, p) => {
        const dist = haversineMeters(d.latitud, d.longitud, p.latitud, p.longitud);
        return Math.min(acc, dist);
      }, Number.POSITIVE_INFINITY);
      return minDist <= maxDistanceToRouteMeters;
    });

    setSegmentosMapa(splitByTimeAndDistance(coordsForMap, SPLIT_GAP_SECONDS / 60));
    setCoordenadas(coordsForMap);
    setDropoffCoords(dropoffsFiltered);
    if (coordsForMap.length > 1) {
      let metros = 0;
      let distanciaMaxEmpresaM = 0;
      for (let i = 1; i < coordsForMap.length; i++) {
        metros += haversineMeters(
          coordsForMap[i - 1].latitud,
          coordsForMap[i - 1].longitud,
          coordsForMap[i].latitud,
          coordsForMap[i].longitud
        );
      }
      coordsForMap.forEach((p) => {
        const d = haversineMeters(p.latitud, p.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng);
        if (d > distanciaMaxEmpresaM) distanciaMaxEmpresaM = d;
      });
      const distInicioTmp = haversineMeters(
        coordsForMap[0].latitud,
        coordsForMap[0].longitud,
        TRAMO_ANCHOR_POINT.lat,
        TRAMO_ANCHOR_POINT.lng
      );
      const distFinTmp = haversineMeters(
        coordsForMap[coordsForMap.length - 1].latitud,
        coordsForMap[coordsForMap.length - 1].longitud,
        TRAMO_ANCHOR_POINT.lat,
        TRAMO_ANCHOR_POINT.lng
      );
      let estado = "Operativo";
      if (distInicioTmp > ANCHOR_MAX_DISTANCE_METERS && distFinTmp > ANCHOR_MAX_DISTANCE_METERS) {
        estado = "Incompleto (no toca empresa)";
      } else if (distanciaMaxEmpresaM <= ANCHOR_MAX_DISTANCE_METERS) {
        estado = "Estacionamiento / Sin recorrido";
      }
      setMetricasTramo({
        kmRecorridos: Number((metros / 1000).toFixed(2)),
        distanciaMaxEmpresaM: Math.round(distanciaMaxEmpresaM),
        estado,
      });
    } else {
      setMetricasTramo({ kmRecorridos: 0, distanciaMaxEmpresaM: 0, estado: "Sin datos" });
    }
    if (coordsForMap.length > 0) {
      const inicio = coordsForMap[0];
      const fin = coordsForMap[coordsForMap.length - 1];
      setDistanciasAnchor({
        inicio: Math.round(
          haversineMeters(inicio.latitud, inicio.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng)
        ),
        fin: Math.round(
          haversineMeters(fin.latitud, fin.longitud, TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng)
        ),
      });
    } else {
      setDistanciasAnchor({ inicio: null, fin: null });
    }
    if (coordsForMap.length === 0) {
      setError("No hay puntos para el tramo seleccionado.");
    } else {
      setError(null);
    }
  };

  const handleConsultar = async () => {
    if (!selectedBus || !fechaDia) {
      setError("Por favor seleccione un bus y una fecha.");
      return;
    }

    setLoading(true);
    setError(null);
    setCoordenadas([]);
    setCoordenadasBaseDia([]);
    setSegmentosDiaRaw([]);
    setSegmentosMapa([]);
    setDropoffCoords([]);
    setDistanciasAnchor({ inicio: null, fin: null });
    setMetricasTramo({ kmRecorridos: 0, distanciaMaxEmpresaM: 0, estado: "Sin datos" });
    setTramosDia([]);
    setSelectedTramo("todos");

    try {
      establecerOrigenConsumo("asistencia:trazabilidad");
      const busSeleccionado = buses.find((b) => b.id === selectedBus);
      if (!busSeleccionado) throw new Error("Bus no encontrado");
      const placaBus = String(busSeleccionado.placa || "").trim();
      if (!placaBus) {
        setError("El bus seleccionado no tiene placa configurada.");
        return;
      }

      const startDateTime = new Date(`${fechaDia}T00:00:00`);
      const endDateTime = new Date(`${fechaDia}T23:59:59.999`);
      const startMinus1 = new Date(startDateTime.getTime() - 24 * 60 * 60 * 1000);
      const endPlus1 = new Date(endDateTime.getTime() + 24 * 60 * 60 * 1000);

      // 1) TRAZA REAL DEL BUS desde ruta_bus/{placa}/coordenadas
      const rutaBusRef = collection(db, `ruta_bus/${placaBus}/coordenadas`);
      const rutaBusQuery = query(
        rutaBusRef,
        where("timestamp", ">=", startMinus1),
        where("timestamp", "<=", endPlus1),
        orderBy("timestamp", "asc")
      );
      const rutaBusSnap = await getDocs(rutaBusQuery);

      if (rutaBusSnap.empty) {
        setError(`No se encontraron coordenadas de ruta_bus para la placa ${placaBus} en la fecha seleccionada.`);
        return;
      }

      const startMs = startDateTime.getTime();
      const endMs = endDateTime.getTime();
      const validCoords = rutaBusSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .map((c) => {
          const lat = Number(c.latitud ?? c.ubicacion?.latitud);
          const lng = Number(c.longitud ?? c.ubicacion?.longitud);
          const timeFromDevice = parseFechaHoraToMs(c.fechaHora);
          let timeFromServer = null;
          if (c.timestamp && typeof c.timestamp.toDate === "function") timeFromServer = c.timestamp.toDate().getTime();
          else if (c.timestamp && typeof c.timestamp === "number") timeFromServer = c.timestamp;
          const normalizedTime = timeFromDevice || timeFromServer || 0;
          return { ...c, latitud: lat, longitud: lng, normalizedTime };
        })
        .filter(
          (c) =>
            Number.isFinite(c.latitud) &&
            Number.isFinite(c.longitud) &&
            (c.latitud !== 0 || c.longitud !== 0) &&
            c.normalizedTime >= startMs &&
            c.normalizedTime <= endMs
        )
        .sort((a, b) => a.normalizedTime - b.normalizedTime);

      if (validCoords.length === 0) {
        setError(`Se encontraron documentos en ruta_bus para ${placaBus}, pero no tienen coordenadas/timestamp válidos.`);
        return;
      }

      // 2) PUNTOS DE USUARIOS (subida/bajada) desde registros
      const registrosQuery = query(
        collectionGroup(db, "registros"),
        where("ruc", "==", rucEmpresa),
        where("fechaJornada", "==", fechaDia)
      );
      const registrosSnap = await getDocs(registrosQuery);
      const puntosUsuario = registrosSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((doc) => {
          const docBusId = doc.busId || doc.metadata?.busId;
          const docPlaca = doc.placa || doc.metadata?.busPlaca || doc.busPlaca;
          return docBusId === selectedBus || String(docPlaca || "").trim() === placaBus;
        })
        .map((c) => {
          const lat = Number(c.latitud ?? c.ubicacion?.latitud);
          const lng = Number(c.longitud ?? c.ubicacion?.longitud);
          let docTime;
          if (c.timestamp && typeof c.timestamp.toDate === "function") docTime = c.timestamp.toDate().getTime();
          else if (c.timestamp && typeof c.timestamp === "number") docTime = c.timestamp;
          else if (c.fechaHora) docTime = new Date(c.fechaHora).getTime();
          return { ...c, latitud: lat, longitud: lng, normalizedTime: docTime || 0 };
        })
        .filter((c) => Number.isFinite(c.latitud) && Number.isFinite(c.longitud) && c.normalizedTime > 0);

      const segmentados = mergeSegmentsByWindow(
        splitByTimeAndDistance(validCoords, SPLIT_GAP_SECONDS / 60),
        MERGE_WINDOW_MINUTES
      );
      const tramos = segmentados.map((segmento, index) => ({
        id: String(index),
        index,
        totalPuntos: segmento.length,
        inicio: segmento[0]?.normalizedTime || 0,
        fin: segmento[segmento.length - 1]?.normalizedTime || 0,
        tipo: classifyTramoByAnchor(segmento),
        startDist: Math.round(
          haversineMeters(
            segmento[0]?.latitud || 0,
            segmento[0]?.longitud || 0,
            TRAMO_ANCHOR_POINT.lat,
            TRAMO_ANCHOR_POINT.lng
          )
        ),
        endDist: Math.round(
          haversineMeters(
            segmento[segmento.length - 1]?.latitud || 0,
            segmento[segmento.length - 1]?.longitud || 0,
            TRAMO_ANCHOR_POINT.lat,
            TRAMO_ANCHOR_POINT.lng
          )
        ),
        label: `${classifyTramoByAnchor(segmento)} ${index + 1} · ${formatDateLabel(segmento[0]?.normalizedTime || Date.now())} ${formatTimeLabel(segmento[0]?.normalizedTime || Date.now())} - ${formatTimeLabel(segmento[segmento.length - 1]?.normalizedTime || Date.now())} · ${formatDurationMinutes(segmento[0]?.normalizedTime || 0, segmento[segmento.length - 1]?.normalizedTime || 0)}`,
      }));
      setTramosDia(tramos);
      setCoordenadasBaseDia(validCoords);
      setSegmentosDiaRaw(segmentados);
      setDropoffCoords(puntosUsuario.filter((c) => isBoardingPoint(c)));
      const tramoPreferido = [...tramos]
        .sort((a, b) => {
          const aMin = Math.min(a.startDist, a.endDist);
          const bMin = Math.min(b.startDist, b.endDist);
          if (aMin !== bMin) return aMin - bMin;
          return a.inicio - b.inicio;
        })[0];
      const tramoInicialId = tramoPreferido ? tramoPreferido.id : "todos";
      setSelectedTramo(tramoInicialId);
      await aplicarTramoEnMapa(validCoords, tramoInicialId, segmentados);
    } catch (err) {
      console.error("Error consultando trazabilidad:", err);
      setError("Error al consultar datos de trazabilidad.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (coordenadasBaseDia.length === 0) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        await aplicarTramoEnMapa(coordenadasBaseDia, selectedTramo, segmentosDiaRaw);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTramo]);

  const handleDemo = async () => {
    setLoading(true);
    setError(null);
    setDropoffCoords([]);
    try {
      const routePoints = [
        { lat: -12.0453, lng: -77.0305 },
        { lat: -12.0465, lng: -77.031 },
        { lat: -12.051, lng: -77.0333 },
        { lat: -12.0533, lng: -77.0347 },
        { lat: -12.0601, lng: -77.0368 },
        { lat: -12.0655, lng: -77.036 },
        { lat: -12.0863, lng: -77.0325 },
        { lat: -12.1028, lng: -77.0298 },
        { lat: -12.1225, lng: -77.0278 },
      ];

      const waypoints = routePoints.map((p) => `${p.lng},${p.lat}`);
      const routeProviders = [
        "https://router.project-osrm.org/route/v1/driving",
        "https://routing.openstreetmap.de/routed-car/route/v1/driving",
      ];

      let routeCoordinates = null;
      for (const provider of routeProviders) {
        try {
          const url = `${provider}/${waypoints.join(";")}?overview=full&geometries=geojson&alternatives=false&steps=false&continue_straight=true`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          if (data.code === "Ok" && data.routes && data.routes.length > 0) {
            routeCoordinates = data.routes[0].geometry.coordinates;
            break;
          }
        } catch (e) {
          console.warn(`Proveedor de rutas no disponible (${provider}):`, e);
        }
      }

      if (!routeCoordinates || routeCoordinates.length === 0) throw new Error("No se pudo obtener ruta de ejemplo");

      const baseTime = new Date().getTime();
      const totalDurationMinutes = 40;
      const timeStep = (totalDurationMinutes * 60000) / routeCoordinates.length;

      const demoCoords = routeCoordinates.map((coord, index) => ({
        id: `demo_osrm_${index}`,
        latitud: coord[1],
        longitud: coord[0],
        normalizedTime: baseTime + index * timeStep,
        placa: "DEMO-PRESENTACION",
        busId: "demo",
      }));

      setCoordenadas(demoCoords);
      setSegmentosMapa([demoCoords]);
      setDistanciasAnchor({ inicio: null, fin: null });
      setMetricasTramo({ kmRecorridos: 0, distanciaMaxEmpresaM: 0, estado: "Simulacro" });
    } catch (err) {
      console.error("Error demo:", err);
      setError("No se pudo conectar con el servicio de rutas. Se cargará una ruta local de respaldo.");
      const baseTime = new Date().getTime();
      const fallbackData = [
        { lat: -12.0453, lng: -77.0305, min: 0 },
        { lat: -12.0465, lng: -77.031, min: 1 },
        { lat: -12.048, lng: -77.0318, min: 2 },
        { lat: -12.0495, lng: -77.0325, min: 3 },
        { lat: -12.051, lng: -77.0333, min: 4 },
        { lat: -12.0533, lng: -77.0347, min: 6 },
        { lat: -12.0565, lng: -77.0357, min: 8 },
        { lat: -12.0601, lng: -77.0368, min: 10 },
        { lat: -12.0655, lng: -77.036, min: 13 },
        { lat: -12.0712, lng: -77.035, min: 16 },
        { lat: -12.0758, lng: -77.0342, min: 18 },
        { lat: -12.082, lng: -77.0332, min: 22 },
        { lat: -12.0863, lng: -77.0325, min: 25 },
        { lat: -12.0945, lng: -77.0312, min: 29 },
        { lat: -12.1028, lng: -77.0298, min: 33 },
        { lat: -12.1115, lng: -77.0285, min: 37 },
        { lat: -12.1198, lng: -77.028, min: 39 },
        { lat: -12.1225, lng: -77.0278, min: 40 },
      ];
      setCoordenadas(
        fallbackData.map((d, i) => ({
          id: `demo_fallback_${i}`,
          latitud: d.lat,
          longitud: d.lng,
          normalizedTime: baseTime + d.min * 60000,
          placa: "DEMO-FALLBACK",
          busId: "demo",
        }))
      );
      setSegmentosMapa([
        fallbackData.map((d, i) => ({
          id: `demo_fallback_${i}`,
          latitud: d.lat,
          longitud: d.lng,
          normalizedTime: baseTime + d.min * 60000,
          placa: "DEMO-FALLBACK",
          busId: "demo",
        })),
      ]);
      setDistanciasAnchor({ inicio: null, fin: null });
      setMetricasTramo({ kmRecorridos: 0, distanciaMaxEmpresaM: 0, estado: "Simulacro" });
    } finally {
      setLoading(false);
    }
  };

  const segmentosRuta = useMemo(() => segmentosMapa, [segmentosMapa]);

  const mapCenter = useMemo(() => {
    if (coordenadas.length > 0) return [coordenadas[0].latitud, coordenadas[0].longitud];
    return [-12.046374, -77.042793];
  }, [coordenadas]);

  return (
    <Container fluid className="py-4">
      <Card
        className="shadow-lg border-0"
        style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
      >
        {/* Header Premium con degradado verde oscuro */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f59e0b",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaMapMarkedAlt className="me-3" />
              Reporte de Trazabilidad GPS
            </h2>
            <p className="mb-0 opacity-75">
              Seguimiento detallado de rutas y geolocalización de unidades
            </p>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Acciones Rápidas */}
          <Card
            className="border-0 mb-4 shadow-sm"
            style={{
              background: "linear-gradient(135deg, #fffcea, #fff9db)",
              borderRadius: 14,
            }}
          >
            <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap align-items-center">
              <Button
                variant="primary"
                onClick={handleConsultar}
                disabled={loading}
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                {loading ? (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                  <FaSearch />
                )}
                Consultar
              </Button>
              <Button
                variant="warning"
                onClick={handleDemo}
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                Simulacro
              </Button>
            </Card.Body>
          </Card>

          {/* Filtros */}
          <div className="bg-white p-4 rounded-4 shadow-sm mb-4 border text-dark">
            <Form>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold small text-uppercase opacity-75">
                      Bus
                    </Form.Label>
                    <Form.Select
                      value={selectedBus}
                      onChange={(e) => setSelectedBus(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">Seleccione un bus...</option>
                      {buses.map((bus) => (
                        <option key={bus.id} value={bus.id}>
                          {bus.placa} - {bus.modelo}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold small text-uppercase opacity-75">
                      Fecha
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaDia}
                      onChange={(e) => setFechaDia(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold small text-uppercase opacity-75">
                      Tramo del día
                    </Form.Label>
                    <Form.Select
                      value={selectedTramo}
                      onChange={(e) => setSelectedTramo(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="todos">Todos los tramos</option>
                      {tramosDia.map((tramo) => (
                        <option key={tramo.id} value={tramo.id}>
                          {tramo.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>

          <div style={{ height: "600px", width: "100%", border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" }}>
            <MapContainer
              center={mapCenter}
              zoom={12}
              style={{ width: "100%", height: "100%" }}
              whenCreated={(map) => { mapRef.current = map; }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[TRAMO_ANCHOR_POINT.lat, TRAMO_ANCHOR_POINT.lng]} icon={empresaIcon}>
                <Popup>Empresa</Popup>
              </Marker>
                {segmentosRuta.map((segmento, index) => (
                  <Polyline
                    key={`seg_${index}`}
                    positions={segmento.map((c) => [c.latitud, c.longitud])}
                    color={index % 2 === 0 ? "#1e63ff" : "#123c9b"}
                    opacity={0.75}
                    weight={4}
                  />
                ))}

                {coordenadas.map((pto, idx) => (
                  <Marker
                    key={pto.id || idx}
                    position={[pto.latitud, pto.longitud]}
                    icon={DefaultIcon}
                  >
                    <Popup>
                      <strong>{idx === 0 ? "Inicio" : idx === coordenadas.length - 1 ? "Fin" : `Punto ${idx + 1}`}</strong><br />
                      {new Date(pto.normalizedTime).toLocaleString()}<br />
                      Lat: {Number(pto.latitud).toFixed(5)}, Lng: {Number(pto.longitud).toFixed(5)}
                    </Popup>
                  </Marker>
                ))}

                {coordenadas.length > 0 && (
                  <>
                    <Marker
                      position={[coordenadas[0].latitud, coordenadas[0].longitud]}
                      icon={startIcon}
                    >
                      <Popup>Inicio del tramo</Popup>
                    </Marker>
                    <Marker
                      position={[coordenadas[coordenadas.length - 1].latitud, coordenadas[coordenadas.length - 1].longitud]}
                      icon={endIcon}
                    >
                      <Popup>Fin del tramo</Popup>
                    </Marker>
                  </>
                )}

                {dropoffCoords.map((pto, idx) => (
                  <Marker
                    key={`drop_${pto.id || idx}`}
                    position={[pto.latitud, pto.longitud]}
                    icon={redIcon}
                  >
                    <Popup>
                      <strong>Ingreso al bus</strong><br />
                      {new Date(pto.normalizedTime).toLocaleString()}<br />
                      Lat: {Number(pto.latitud).toFixed(5)}, Lng: {Number(pto.longitud).toFixed(5)}
                    </Popup>
                  </Marker>
                ))}

              {coordenadas.length > 0 && <ChangeView bounds={coordenadas.map((c) => [c.latitud, c.longitud])} />}
            </MapContainer>
          </div>

          {coordenadas.length > 0 && (
            <Row className="mt-3">
              <Col md={6} className="text-muted">
                <small>Total puntos rastreados: {coordenadas.length}</small>
              </Col>
              <Col md={6} className="text-md-end">
                <small className="text-muted">
                  Distancia a empresa · Inicio:{" "}
                  <strong>{distanciasAnchor.inicio != null ? `${distanciasAnchor.inicio} m` : "-"}</strong>{" "}
                  | Fin:{" "}
                  <strong>{distanciasAnchor.fin != null ? `${distanciasAnchor.fin} m` : "-"}</strong>
                </small>
              </Col>
              <Col md={12} className="mt-1 text-md-end">
                <small className="text-muted">
                  Recorrido: <strong>{metricasTramo.kmRecorridos} km</strong> | Distancia máxima a empresa:{" "}
                  <strong>{metricasTramo.distanciaMaxEmpresaM} m</strong> | Estado:{" "}
                  <strong>{metricasTramo.estado}</strong>
                </small>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ReporteTrazabilidad;
